import { ChatDocument, ChatMessageDocument } from '@models/chat';
import {
  PAYMENT_REQUEST_MSG_PREFIX,
  PaymentRequestDocument,
  PaymentRequestState,
} from '@models/payment-request';
import { FirebaseCallable, db, functions, httpsCallable } from '@util/firebase';
import { formatCurrency } from '@util/index';
import {
  CollectionReference,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

const paymentRequestsRef = collection(
  db,
  'payment_requests'
) as CollectionReference<PaymentRequestDocument>;

const messagesRef = collection(db, 'messages');

const getDocRef = (id: string) => doc(paymentRequestsRef, id);

export function getPaymentRequestId() {
  return doc(paymentRequestsRef).id;
}

export function buildPaymentRequestMessage(
  amount: number,
  description: string
) {
  const trimmed = description.trim();
  const base = `${PAYMENT_REQUEST_MSG_PREFIX} ${formatCurrency(amount)}`;
  return trimmed ? `${base} - ${trimmed}` : base;
}

export async function createPaymentRequest({
  request,
  chat,
}: {
  request: PaymentRequestDocument;
  chat: ChatDocument;
}) {
  await setDoc(getDocRef(request.id), request);

  const message: ChatMessageDocument = {
    uid: request.requester_uid,
    content: buildPaymentRequestMessage(request.amount, request.description),
    created_at: Date.now(),
    is_img: false,
    is_auto: false,
    payment_request_id: request.id,
  };

  // Bypass chatSchema parse path so payment_request_id can't be stripped.
  const chatRef = doc(messagesRef, chat.id);
  const now = Date.now();
  await updateDoc(chatRef, {
    messages: arrayUnion(message),
    last_time: now,
    [`unread.${request.payer_uid}`]: true,
    [`unread.${request.requester_uid}`]: false,
  });

  return request;
}

export async function getPaymentRequest(id: string) {
  if (!id) return null;
  const snap = await getDoc(getDocRef(id));
  return snap.data() ?? null;
}

/**
 * Every payment request raised against any of these orders.
 *
 * Support reads these a screenful of orders at a time, so the ids are queried in
 * batches: `in` takes at most 30 values, and one request per order would be a
 * read per row.
 */
export async function getPaymentRequestsByOrderIds(orderIds: string[]) {
  const ids = [...new Set(orderIds.filter(Boolean))];
  if (!ids.length) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const snap = await getDocs(
        query(paymentRequestsRef, where('order_id', 'in', chunk))
      );
      return snap.docs.map((doc) => doc.data());
    })
  );
  return results.flat();
}

export function subscribeToPaymentRequest(
  id: string,
  cb: (req: PaymentRequestDocument | null) => void
) {
  return onSnapshot(getDocRef(id), (snap) => {
    cb(snap.data() ?? null);
  });
}

// only the payer can fail a payment request
export async function failedPaymentRequest(
  id: string,
  updates: Pick<PaymentRequestDocument, 'state' | 'failure_reason'>
) {
  await updateDoc(getDocRef(id), updates);
}

// only the requester can cancel a payment request
export async function cancelPaymentRequest(id: string) {
  await updateDoc(getDocRef(id), { state: PaymentRequestState.Cancelled });
}

export type PaymentRequestIntent = {
  paymentIntentId: string;
  paymentIntentClientSecret: string;
  customerId: string;
  ephemeralKey: string;
};

export async function createPaymentRequestIntent(
  paymentRequestId: string
): Promise<PaymentRequestIntent> {
  const { data } = await httpsCallable<
    { payment_request_id: string },
    {
      payment_intent_id: string;
      payment_intent_client_secret: string;
      customer_id: string;
      ephemeral_key: string;
    }
  >(
    functions,
    FirebaseCallable.createPaymentRequestIntent
  )({ payment_request_id: paymentRequestId });
  return {
    paymentIntentId: data.payment_intent_id,
    paymentIntentClientSecret: data.payment_intent_client_secret,
    customerId: data.customer_id,
    ephemeralKey: data.ephemeral_key,
  };
}
